import { getCustomAgesIds, transformGendersToBE, transformGeolocationsToBE } from '../../../lineItemHelpers'
import { formatAudiencesToBE } from '../../../LineItemForms/LineItemDv360Create/LineItemDv360CreateForm/LineItemDV360AudienceForm/formatters'
import { insertIf } from '../../../../../helpers/common'
import { formatSafetyOptionToBE } from './helpers'

import {
  AUDIENCES_OPTION,
  CUSTOM_AUDIENCES_EXCLUSION_CHECKED
} from '../../../../ReusableFormFields/Audiences/AudiencesFields/fields'
import { OPTION_YES } from '../../../../../constants/forms'
import {
  SUSPICIOUS_ACTIVITY,
  ADULT,
  ALCOHOL,
  APPLY_THIRD_PARTY_VERIFIER,
  APPS,
  BLOCK_UNRATED_URLS,
  CATEGORIES,
  CHANNELS,
  CHANNELS_EXCLUDED,
  CONTENT_INSTREAM_POSITIONS,
  CONTENT_OUTSTREAM_POSITIONS,
  CUSTOM_EXCHANGES,
  CUSTOM_POSITIONS,
  CUSTOM_SEGMENT_IDS,
  DRUGS,
  EXCHANGE_OPTION,
  EXCLUDE_UNRATEABLE,
  EXCLUDED_CATEGORIES,
  HATE_SPEECH,
  ILLEGAL_DOWNLOADS,
  KEYWORD_EXCLUSION_LISTS,
  KEYWORDS_TABLE,
  OFFENSIVE_LANGUAGE,
  ON_SCREEN_POSITIONS,
  POSITION_OPTION,
  TARGET_SPECIFIC_CATEGORIES_OR_KEYWORDS,
  TARGET_SPECIFIC_URLS_OR_APPS,
  URLS,
  VIOLENCE,
  YES_USING_PRE_SAVED_KEYWORD_EXCLUSION_LISTS,
  YES_USING_PRE_SAVED_SITE_LISTS,
  VIEWABILITY,
  VIEWABILITY_OPTION,
  UREATED_URLS_SEGMENT_ID
} from './fields'
import { SHOW_COUNTRIES, SHOW_REGIONS } from '../../../../ReusableFormFields/GeolocationsFields/fields'
import { LANGUAGES } from '../../../../ReusableFormFields/LanguagesFields/fields'
import { targetingTypes } from '../../../LineItemForms/LineItemDv360Create/fields'

const getCustomSegmentIds = values => {
  const customSegmentIds = values[CUSTOM_SEGMENT_IDS].map(item => item.text)

  // we need to add the ID 1000001 to the list of segments(customSegmentId field) when checkbox is checked
  if (values[BLOCK_UNRATED_URLS] && !customSegmentIds.includes(UREATED_URLS_SEGMENT_ID)) {
    customSegmentIds.push(UREATED_URLS_SEGMENT_ID)
  }

  return customSegmentIds
}

export const formatThirdPartyVerifierToBE = values => {
  return [
    {
      targeting_type: targetingTypes.TARGETING_TYPE_THIRD_PARTY_VERIFIER,
      assigned_targeting_options: [
        {
          integral_ad_science: {
            excludeUnrateable: values[EXCLUDE_UNRATEABLE] === OPTION_YES,
            excludedAdultRisk: formatSafetyOptionToBE(ADULT, values),
            excludedAlcoholRisk: formatSafetyOptionToBE(ALCOHOL, values),
            excludedIllegalDownloadsRisk: formatSafetyOptionToBE(ILLEGAL_DOWNLOADS, values),
            excludedDrugsRisk: formatSafetyOptionToBE(DRUGS, values),
            excludedHateSpeechRisk: formatSafetyOptionToBE(HATE_SPEECH, values),
            excludedOffensiveLanguageRisk: formatSafetyOptionToBE(OFFENSIVE_LANGUAGE, values),
            excludedViolenceRisk: formatSafetyOptionToBE(VIOLENCE, values),
            excludedAdFraudRisk: formatSafetyOptionToBE(SUSPICIOUS_ACTIVITY, values),
            customSegmentId: getCustomSegmentIds(values)
          }
        }
      ]
    }
  ]
}

export const formatTargetingRequestsToBE = values => {
  // If users don't select any audiences, we don't need to send request to BE to update targeting options
  // in this case we just set successSubmit to true, and switch to next form
  const shouldSendAudience = values[AUDIENCES_OPTION] === OPTION_YES || values[CUSTOM_AUDIENCES_EXCLUSION_CHECKED]

  const filledUrls = values[URLS].filter(urlTableRow => urlTableRow?.[0]?.value).map(
    urlTableRow => urlTableRow?.[0]?.value
  )
  const filledExcludedUrls = values[URLS].filter(urlTableRow => urlTableRow?.[1]?.value).map(
    urlTableRow => urlTableRow?.[1]?.value
  )
  const filledApps = values[APPS].filter(appTableRos => appTableRos?.[0]?.value).map(
    appTableRos => appTableRos?.[0]?.value
  )
  const filledExcludedApps = values[APPS].filter(appTableRos => appTableRos?.[1]?.value).map(
    appTableRos => appTableRos?.[1]?.value
  )

  const filledCategories = values[CATEGORIES].filter(category => category?.value)
  const filledExcludedCategories = values[EXCLUDED_CATEGORIES].filter(category => category?.value)

  const shouldSendUrls =
    values[TARGET_SPECIFIC_URLS_OR_APPS] === OPTION_YES && (filledUrls.length || filledExcludedUrls.length)
  const shouldSendApps =
    values[TARGET_SPECIFIC_URLS_OR_APPS] === OPTION_YES && (filledApps.length || filledExcludedApps.length)
  const shouldSendCategories =
    values[TARGET_SPECIFIC_CATEGORIES_OR_KEYWORDS] === OPTION_YES &&
    (filledCategories.length || filledExcludedCategories.length)

  const filledKeywords = values[KEYWORDS_TABLE].filter(keywordRow => keywordRow[0]).map(keywordRow => keywordRow[0])
  const filledExcludedKeywords = values[KEYWORDS_TABLE].filter(keywordRow => keywordRow[1]).map(
    keywordRow => keywordRow[1]
  )

  const shouldSendKeywords =
    values[TARGET_SPECIFIC_CATEGORIES_OR_KEYWORDS] === OPTION_YES &&
    (filledKeywords.length || filledExcludedKeywords.length)

  const countries = values[SHOW_COUNTRIES]
  const regions = values[SHOW_REGIONS]

  const shouldSendGeolocations = countries.length || regions.length

  const genders = transformGendersToBE(values)
  const shouldSendGenders = genders.length

  const languages = values[LANGUAGES]
  const shouldSendLanguages = languages.length

  const ages = getCustomAgesIds(values)
  const shouldSendAges = ages.length

  const shouldSendExchanges = values[EXCHANGE_OPTION] === CUSTOM_EXCHANGES

  const shouldSendOnScreenPositions = values[POSITION_OPTION] === CUSTOM_POSITIONS && values[ON_SCREEN_POSITIONS].length
  const shouldSendContentInstreamPositions =
    values[POSITION_OPTION] === CUSTOM_POSITIONS && values[CONTENT_INSTREAM_POSITIONS].length
  const shouldSendContentOutstreamPositions =
    values[POSITION_OPTION] === CUSTOM_POSITIONS && values[CONTENT_OUTSTREAM_POSITIONS].length

  const shouldSendThirdPartyVerifierTargeting = values[APPLY_THIRD_PARTY_VERIFIER] === OPTION_YES

  const channels = values[CHANNELS]
  const excludedChannels = values[CHANNELS_EXCLUDED]

  const keywordExclusionLists = values[KEYWORD_EXCLUSION_LISTS]

  const shouldSendChannels =
    values[TARGET_SPECIFIC_URLS_OR_APPS] === YES_USING_PRE_SAVED_SITE_LISTS &&
    (channels.length || excludedChannels.length)

  const shouldSendKeywordExclusionLists =
    values[TARGET_SPECIFIC_CATEGORIES_OR_KEYWORDS] === YES_USING_PRE_SAVED_KEYWORD_EXCLUSION_LISTS &&
    keywordExclusionLists.length

  const shouldSendViewability = values[VIEWABILITY_OPTION] === OPTION_YES && values[VIEWABILITY]

  const audienceTargeting = [
    {
      targeting_type: targetingTypes.TARGETING_TYPE_AUDIENCE_GROUP,
      assigned_targeting_options: formatAudiencesToBE(values)
    }
  ]

  const geolocationsTargeting = [
    {
      targeting_type: targetingTypes.TARGETING_TYPE_GEO_REGION,
      assigned_targeting_options: transformGeolocationsToBE(values)
    }
  ]

  const urlsTargeting = [
    {
      targeting_type: targetingTypes.TARGETING_TYPE_URL,
      assigned_targeting_options: [
        ...filledUrls.map(url => ({
          url: url
        })),
        ...filledExcludedUrls.map(url => ({
          url: url,
          negative: true
        }))
      ]
    }
  ]

  const appsTargeting = [
    {
      targeting_type: targetingTypes.TARGETING_TYPE_APP,
      assigned_targeting_options: [
        ...filledApps.map(app => ({
          app_id: app
        })),
        ...filledExcludedApps.map(app => ({
          app_id: app,
          negative: true
        }))
      ]
    }
  ]

  const categoriesTargeting = [
    {
      targeting_type: targetingTypes.TARGETING_TYPE_CATEGORY,
      assigned_targeting_options: [
        ...values[CATEGORIES].map(category => ({
          targeting_option_id: category.value,
          negative: false
        })),
        ...values[EXCLUDED_CATEGORIES].map(category => ({
          targeting_option_id: category.value,
          negative: true
        }))
      ]
    }
  ]

  const keywordsTargeting = [
    {
      targeting_type: targetingTypes.TARGETING_TYPE_KEYWORD,
      assigned_targeting_options: [
        ...filledKeywords.map(keyword => ({
          keyword: keyword.value
        })),
        ...filledExcludedKeywords.map(keyword => ({
          keyword: keyword.value,
          negative: true
        }))
      ]
    }
  ]

  const genderTargeting = [
    {
      targeting_type: targetingTypes.TARGETING_TYPE_GENDER,
      assigned_targeting_options: genders.map(gender => ({
        gender
      }))
    }
  ]

  const languagesTargeting = [
    {
      targeting_type: targetingTypes.TARGETING_TYPE_LANGUAGE,
      assigned_targeting_options: languages.map(item => ({
        targeting_option_id: item.value
      }))
    }
  ]

  const agesTargeting = [
    {
      targeting_type: targetingTypes.TARGETING_TYPE_AGE_RANGE,
      assigned_targeting_options: ages.map(item => ({
        age_range: item
      }))
    }
  ]

  const exchangesTargeting = [
    {
      targeting_type: targetingTypes.TARGETING_TYPE_EXCHANGE,
      assigned_targeting_options: values[CUSTOM_EXCHANGES].map(item => ({
        exchange: item.value
      }))
    }
  ]

  const onScreenPositionsTargeting = [
    {
      targeting_type: targetingTypes.TARGETING_TYPE_ON_SCREEN_POSITION,
      assigned_targeting_options: values[ON_SCREEN_POSITIONS].map(item => ({
        on_screen_position: item.value,
        targeting_option_id: item.targetingOptionId
      }))
    }
  ]

  const contentInstreamPositionsTargeting = [
    {
      targeting_type: targetingTypes.TARGETING_TYPE_CONTENT_INSTREAM_POSITION,
      assigned_targeting_options: values[CONTENT_INSTREAM_POSITIONS].map(item => ({
        content_instream_position: item.value,
        targeting_option_id: item.targetingOptionId
      }))
    }
  ]

  const contentOutstreamPositionsTargeting = [
    {
      targeting_type: targetingTypes.TARGETING_TYPE_CONTENT_OUTSTREAM_POSITION,
      assigned_targeting_options: values[CONTENT_OUTSTREAM_POSITIONS].map(item => ({
        content_outstream_position: item.value,
        targeting_option_id: item.targetingOptionId
      }))
    }
  ]

  const thirdPartyVerifierTargeting = formatThirdPartyVerifierToBE(values)

  const channelsTargeting = [
    {
      targeting_type: targetingTypes.TARGETING_TYPE_CHANNEL,
      assigned_targeting_options: [
        ...channels.map(channel => ({
          channel_id: channel.value
        })),
        ...excludedChannels.map(channel => ({
          channel_id: channel.value,
          negative: true
        }))
      ]
    }
  ]

  const keywordExclusionTargeting = [
    {
      targeting_type: targetingTypes.TARGETING_TYPE_NEGATIVE_KEYWORD_LIST,
      assigned_targeting_options: keywordExclusionLists.map(item => ({
        negative_keyword_list_id: item.value
      }))
    }
  ]

  const viewabilityTargeting = [
    {
      targeting_type: targetingTypes.TARGETING_TYPE_VIEWABILITY,
      assigned_targeting_options: [
        {
          viewability: values[VIEWABILITY]
        }
      ]
    }
  ]

  const requests = [
    ...insertIf(shouldSendAudience, ...audienceTargeting),
    ...insertIf(shouldSendGeolocations, ...geolocationsTargeting),
    ...insertIf(shouldSendUrls, ...urlsTargeting),
    ...insertIf(shouldSendApps, ...appsTargeting),
    ...insertIf(shouldSendCategories, ...categoriesTargeting),
    ...insertIf(shouldSendKeywords, ...keywordsTargeting),
    ...insertIf(shouldSendGenders, ...genderTargeting),
    ...insertIf(shouldSendLanguages, ...languagesTargeting),
    ...insertIf(shouldSendAges, ...agesTargeting),
    ...insertIf(shouldSendExchanges, ...exchangesTargeting),
    ...insertIf(shouldSendOnScreenPositions, ...onScreenPositionsTargeting),
    ...insertIf(shouldSendContentInstreamPositions, ...contentInstreamPositionsTargeting),
    ...insertIf(shouldSendContentOutstreamPositions, ...contentOutstreamPositionsTargeting),
    ...insertIf(shouldSendThirdPartyVerifierTargeting, ...thirdPartyVerifierTargeting),
    ...insertIf(shouldSendChannels, ...channelsTargeting),
    ...insertIf(shouldSendKeywordExclusionLists, ...keywordExclusionTargeting),
    ...insertIf(shouldSendViewability, ...viewabilityTargeting)
  ]

  return requests
}
